import { useCallback, useEffect, useMemo } from 'react';

import { useCurrentOrg } from 'new/hooks/adapters/useCurrentOrg';
import { useCanManageCapsules } from 'new/modules/settings/capsules/hooks/useCanManageCapsules';
import type { CapsuleEntity } from 'new/stores/capsules/capsule.slice';
import type { CapsuleState } from 'new/stores/capsules/capsule.slice';
import { ClusterType } from 'new/stores/capsules/capsules.endpoints';
import type { CapsuleDto, CreateClusterPayload } from 'new/stores/capsules/capsules.endpoints';
import type { CapsuleTableRow } from 'new/modules/settings/capsules/viewModels/capsulesTableViewModel';
import { mapCapsuleToTableRow } from 'new/modules/settings/capsules/utils/mapCapsuleToTableRow';
import { useCapsulesAdapter, useCapsulesRepository } from 'new/state/capsules';

/**
 * Input type for registering a new capsule cluster.
 */
export type RegisterCapsuleClusterInput = {
  name: string;
  description: string;
};

/**
 * Facade hook for the Capsules Settings screen.
 *
 * ## Data Flow Architecture
 *
 * This hook consolidates all data access and mutations for the capsules settings screen.
 * Understanding the flow between RTK Query, listeners, and the slice is important:
 *
 * ### List (rows) comes from the Redux slice
 * - The `rows` returned by this hook are computed from the `capsule.slice` state.
 * - The slice is populated by **listeners** (`capsules.listeners.ts`) when RTK Query
 *   queries are fulfilled.
 * - When `getCapsules` query fulfills → `setCapsules` is dispatched → slice updates.
 * - When `createCluster`/`updateCapsule` fulfills → `upsertCapsule` is dispatched.
 * - When `deleteCapsule` fulfills → `removeCapsule` is dispatched.
 *
 * ### Mutations are thin RTK Query wrappers
 * - `updateCapsule` / `deleteCapsule` / `registerCapsule` are thin wrappers around
 *   RTK Query mutations.
 * - They don't directly modify the slice; they trigger the API call.
 * - The **listeners** handle `matchFulfilled` and update the slice via
 *   `upsertCapsule` / `removeCapsule`.
 * - This ensures UI stays in sync with the API cache and prevents race conditions.
 *
 * ### Why this architecture?
 * - **Single source of truth**: The slice holds the canonical list for the UI.
 * - **Cache invalidation**: RTK Query invalidates tags, listeners sync slice to cache.
 * - **Optimistic updates**: Can be added at the listener level if needed.
 *
 * @returns Object containing:
 * - `rows`: Table rows computed from slice entities
 * - `refetch`: Function to refetch the capsules list
 * - `isLoading`, `isFetching`, `isError`, `error`: Query state flags
 * - `registerCapsule`: Function to create a new capsule cluster
 * - `isCreating`: Loading state for cluster creation
 * - `updateCapsule`: RTKQ mutation trigger for updating a capsule
 * - `deleteCapsule`: RTKQ mutation trigger for deleting a capsule
 */
export function useCapsulesSettings() {
  const orgId = useCurrentOrg()?.id;
  const { canManage: canRegisterCapsule } = useCanManageCapsules();

  const {
    capsules: capsuleState,
    fetchCapsules,
    isLoading,
    isFetching,
    isError,
    error,
  } = useCapsulesRepository();
  const { clearCapsules, createCluster, isCreating, updateCapsule, deleteCapsule } =
    useCapsulesAdapter();

  const rows = useMemo(() => {
    const { byId, entities }: CapsuleState = capsuleState;

    const capsuleEntitiesInListOrder = byId
      .map((capsuleId) => entities[capsuleId])
      .filter(
        (entity): entity is CapsuleEntity =>
          entity != null && entity.cluster_type === ClusterType.CAPSULE,
      );

    const capsuleTableRows = capsuleEntitiesInListOrder.map((capsuleEntity) =>
      mapCapsuleToTableRow(capsuleEntity),
    ) as CapsuleTableRow[];

    return capsuleTableRows;
  }, [capsuleState]);

  useEffect(() => {
    if (orgId == null) return;
    clearCapsules();
    fetchCapsules({ org_id: orgId });
  }, [orgId, clearCapsules, fetchCapsules]);

  const refetch = useCallback(() => {
    if (orgId == null) return;
    clearCapsules();
    return fetchCapsules({ org_id: orgId });
  }, [fetchCapsules, orgId, clearCapsules]);

  const registerCapsule = useCallback(
    async (input: RegisterCapsuleClusterInput): Promise<CapsuleDto> => {
      if (!canRegisterCapsule) throw new Error('You do not have permission to register capsules.');
      if (orgId == null) throw new Error('No organization selected.');

      const trimmedName = input.name.trim();
      const trimmedDescription = input.description.trim();

      const createClusterPayload: CreateClusterPayload = {
        org_id: orgId,
        name: trimmedName,
        region: 'us-east-1',
        description: trimmedDescription,
        cluster_type: ClusterType.CAPSULE,
      };

      return createCluster(createClusterPayload).unwrap();
    },
    [canRegisterCapsule, createCluster, orgId],
  );

  return {
    rows,
    refetch,
    isLoading,
    isFetching,
    isError,
    error,
    canRegisterCapsule,
    registerCapsule,
    isCreating,
    updateCapsule,
    deleteCapsule,
  };
}
